/*
 * <quantity-stepper> — Flink-style quantity control.
 *
 * Two skins, one behaviour:
 *   - on product cards it is the familiar add-to-cart circle that grows
 *     leftwards into a pill (component-quantity-stepper.css)
 *   - on the product page it is the full-width CTA that shrinks rightwards
 *     into a pill (component-cta-stepper.css)
 *
 * In both cases the element is a MIRROR OF THE CART, not a pre-selection: the
 * number shown is the quantity in the cart, and taking it to 0 removes the
 * line. The open/closed state is a pure function of that quantity — no timer.
 *
 * The pill's open/closed transition and the cart's DOM work are kept apart.
 * Refreshing the mini-cart means parsing ~20KB of section HTML and swapping it
 * in, which measured 8.5ms on a desktop CPU and lands about 220ms into a 340ms
 * animation — squarely in the middle of it. Throttled to phone-class speed that
 * cost 20 dropped frames over five runs, worst 109ms. Holding it until the pill
 * has settled brought that to zero. The request still goes out immediately; only
 * the rendering waits.
 *
 * Requests are optimistic and coalesced: the number on screen (target) moves
 * immediately, while at most ONE request is in flight. When a response lands
 * and target has moved on, a single follow-up request carries the final value.
 * Five fast taps therefore produce two requests, not five, and the cart can
 * never end up behind or ahead of what the customer saw.
 */
if (!customElements.get('quantity-stepper')) {
  class QuantityStepper extends HTMLElement {
    static instances = [];
    static cartPromise = null;
    static listening = false;
    // Longest pill transition (400ms on the product page CTA) plus a frame.
    static SETTLE_MS = 420;

    connectedCallback() {
      this.variantId = String(this.dataset.variantId || '');
      this.max = this.dataset.max ? parseInt(this.dataset.max, 10) : Infinity;
      if (Number.isNaN(this.max) || this.max < 0) this.max = 0;
      this.target = 0; // what the UI shows
      this.actual = 0; // what the server confirmed
      this.inFlight = false;
      this.unavailable = this.hasAttribute('data-unavailable');

      // The card skin marks its parts by class, the CTA skin by data attribute.
      this.valueEl = this.querySelector('[data-stepper-value], .quantity-stepper__value');
      // The CTA has two add buttons: the glyph inside the open pill and the
      // labelled pill itself while it is closed. Both increase.
      this.plusEls = Array.from(this.querySelectorAll('[data-stepper-plus], .quantity-stepper__button--plus'));
      this.plusEl = this.plusEls[0];
      this.labelEl = this.querySelector('[data-stepper-label]');

      this.plusEls.forEach((el) => el.addEventListener('click', () => this.step(1)));
      this.querySelector('[data-stepper-minus], .quantity-stepper__button--minus')
        .addEventListener('click', () => this.step(-1));

      // Product page only: follow the variant picker.
      if (this.hasAttribute('data-track-variant')) {
        this.variantListener = (event) => this.setVariant(event.detail && event.detail.variant);
        document.addEventListener('variant:change', this.variantListener);
      }

      QuantityStepper.instances.push(this);
      QuantityStepper.initFromCart();
      QuantityStepper.listen();
    }

    disconnectedCallback() {
      QuantityStepper.instances = QuantityStepper.instances.filter((el) => el !== this);
      if (this.variantListener) document.removeEventListener('variant:change', this.variantListener);
    }

    // The same variant can sit in several sliders on one page — and on the
    // product page the CTA and a recommendation card can be the same item.
    get siblings() {
      return QuantityStepper.instances.filter((el) => el !== this && el.variantId === this.variantId);
    }

    step(delta) {
      if (this.unavailable) return;
      const next = Math.min(Math.max(this.target + delta, 0), this.max);
      if (next === this.target) return;
      // Only crossing 0 moves the pill; 3 -> 4 just changes the number, and
      // there is nothing to stay out of the way of.
      if ((next > 0) !== (this.target > 0)) {
        this.settleAt = performance.now() + QuantityStepper.SETTLE_MS;
      }
      this.target = next;
      this.render();
      this.siblings.forEach((el) => { el.target = next; el.render(); });
      this.flush();
    }

    // Resolves once the pill has stopped moving. Everything expensive about a
    // cart response hangs off this: parsing it (the section HTML rides along in
    // the JSON, so this is not cheap) and swapping the mini-cart in.
    settled() {
      const wait = (this.settleAt || 0) - performance.now();
      return wait <= 0 ? Promise.resolve() : new Promise((resolve) => setTimeout(resolve, wait));
    }

    render() {
      const active = this.target > 0;
      this.classList.toggle('is-active', active);
      this.valueEl.textContent = this.target;
      const blocked = this.unavailable || this.target >= this.max;
      this.plusEls.forEach((el) => el.toggleAttribute('disabled', blocked));
      // On a card the plus IS the add-to-cart button while collapsed, so its
      // label swaps. The CTA carries its wording in the pill instead.
      const label = active ? this.plusEl.dataset.labelIncrease : this.plusEl.dataset.labelAdd;
      if (label) this.plusEl.setAttribute('aria-label', label);
    }

    /* Product page: the variant picker changed under us. The variant JSON the
       theme ships carries availability but no inventory count, so the stock cap
       is dropped and the cart API is left to reject an overshoot — resync()
       then picks up the truth. */
    setVariant(variant) {
      if (!variant) return;

      this.unavailable = variant.available === false;
      this.toggleAttribute('data-unavailable', this.unavailable);
      if (this.labelEl) {
        const text = this.unavailable ? this.labelEl.dataset.labelSoldOut : this.labelEl.dataset.labelAdd;
        if (text) this.labelEl.textContent = text;
        this.labelEl.toggleAttribute('disabled', this.unavailable);
      }

      const id = String(variant.id);
      if (this.variantId === id) {
        this.render();
        return;
      }

      this.variantId = id;
      this.dataset.variantId = id;
      this.max = Infinity;
      this.target = 0;
      this.actual = 0;
      this.render();
      this.resync();
    }

    flush() {
      if (this.inFlight || this.target === this.actual) return;
      this.inFlight = true;

      // Pin the variant for this round trip: the picker can move while the
      // request is out, and the answer must not be applied to the new one.
      const variantId = this.variantId;
      const desired = this.target;
      const adding = this.actual === 0;
      const body = JSON.stringify({
        id: variantId,
        quantity: desired,
        sections: ['mini-cart', 'cart-icon-bubble', 'mobile-cart-icon-bubble'],
        sections_url: window.location.pathname,
      });
      const url = adding ? window.routes.cart_add_url : window.routes.cart_change_url;

      fetch(url, { ...fetchConfig('javascript'), body })
        // Hold the body unread until the pill has landed. The network has it
        // buffered either way; parsing it early only costs frames.
        .then((response) => this.settled().then(() => response.json()))
        .then((state) => {
          if (this.variantId !== variantId) return; // picker moved: setVariant owns the state now

          if (state.status && state.status !== 200) {
            // Usually 422: not enough stock. Resync from the cart instead of guessing.
            document.dispatchEvent(new CustomEvent('ajaxProduct:error', {
              detail: { errorMessage: state.description },
            }));
            return this.resync();
          }

          if (adding) {
            this.actual = desired;
            // renderContents updates the mini-cart and the icon bubbles.
            document.querySelector('mini-cart')?.renderContents(state);
          } else {
            // /cart/change.js answers with the whole cart: take the truth from it.
            const line = (state.items || []).find((item) => String(item.variant_id) === variantId);
            this.actual = line ? line.quantity : 0;
            this.updateSections(state.sections);
          }
          this.siblings.forEach((el) => { el.actual = this.actual; });
        })
        .catch(() => this.resync())
        .finally(() => {
          this.inFlight = false;
          if (this.variantId !== variantId) return;
          if (this.target !== this.actual) {
            this.flush();
          } else {
            // Quantity settled somewhere below the request (stock cap): show it.
            this.render();
            this.siblings.forEach((el) => { el.target = el.actual; el.render(); });
          }
        });
    }

    // Quiet section update for plus/minus on an existing line.
    updateSections(sections) {
      if (!sections) return;
      ['mini-cart', 'cart-icon-bubble', 'mobile-cart-icon-bubble'].forEach((id) => {
        const element = document.getElementById(id);
        if (!element || !sections[id]) return;
        const fresh = new DOMParser().parseFromString(sections[id], 'text/html').querySelector('.shopify-section');
        if (fresh) element.innerHTML = fresh.innerHTML;
      });
    }

    resync() {
      return fetch(`${window.routes.cart_url}.js`)
        .then((response) => response.json())
        .then((cart) => {
          const line = (cart.items || []).find((item) => String(item.variant_id) === this.variantId);
          this.actual = line ? line.quantity : 0;
          this.target = this.actual;
          this.render();
          this.siblings.forEach((el) => {
            el.actual = this.actual;
            el.target = this.actual;
            el.render();
          });
        })
        .catch(() => {});
    }

    /* A stepper only ever heard about changes it made itself, so removing the
       line from the cart drawer left its pill sitting there showing 1 for an
       item no longer in the cart. These are the two places the cart moves
       without a stepper being involved. */
    static listen() {
      if (QuantityStepper.listening) return;
      QuantityStepper.listening = true;

      // Drawer and cart page, via cart.js — carries the new cart as a string.
      document.addEventListener('cart:updated', (event) => {
        const raw = event.detail && event.detail.cart;
        let cart = null;
        try {
          cart = typeof raw === 'string' ? JSON.parse(raw) : raw;
        } catch (error) {
          /* fall through to re-reading it */
        }
        if (cart && cart.items) QuantityStepper.applyCart(cart);
        else QuantityStepper.refresh();
      });

      // The product page form adds without telling any stepper.
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        subscribe(PUB_SUB_EVENTS.cartUpdate, () => QuantityStepper.refresh());
      }
    }

    static applyCart(cart) {
      QuantityStepper.instances.forEach((el) => {
        // A stepper waiting on its own response knows better than we do.
        if (el.inFlight) return;
        const line = (cart.items || []).find((item) => String(item.variant_id) === el.variantId);
        el.actual = line ? line.quantity : 0;
        el.target = el.actual;
        el.render();
      });
    }

    static refresh() {
      return fetch(`${window.routes.cart_url}.js`)
        .then((response) => response.json())
        .then((cart) => QuantityStepper.applyCart(cart))
        .catch(() => {});
    }

    // One /cart.js request per page load seeds every stepper with what is
    // already in the cart, so the pill survives reloads and navigation.
    static initFromCart() {
      if (!QuantityStepper.cartPromise) {
        QuantityStepper.cartPromise = fetch(`${window.routes.cart_url}.js`)
          .then((response) => response.json())
          .catch(() => ({ items: [] }));
      }
      QuantityStepper.cartPromise.then((cart) => {
        QuantityStepper.instances.forEach((el) => {
          if (el.seeded || el.inFlight || el.target !== el.actual) return;
          el.seeded = true;
          const line = (cart.items || []).find((item) => String(item.variant_id) === el.variantId);
          el.actual = line ? line.quantity : 0;
          el.target = el.actual;
          el.render();
        });
      });
    }
  }

  customElements.define('quantity-stepper', QuantityStepper);
}
